import {
  Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges
} from '@angular/core';
import { Observable, Subject, merge, of } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { RenderConfigurationService } from 'render/services/render-configuration.service';
import { UnsubscribeComponent } from 'shared/directives/unsubscribe.component';
import { IdtComponent } from 'shared/models/idt-components/idt-component.models';
import { AssetsSharedService } from 'shared/services/assets-shared.service';
import { Link } from 'shared/services/links.model';
import { LinksService } from 'shared/services/links.service';


@Component({
  selector: 'render-link[link]',
  templateUrl: './render-link.component.html',
  styleUrls: ['./render-link.component.scss']
})
export class RenderLinkComponent extends UnsubscribeComponent implements OnInit, OnChanges, OnDestroy {

  @Input() public link!: Link;
  @Input() public lang: string | undefined;
  @Input() public styleClass: string | undefined;
  @Input() public piece: IdtComponent | undefined;

  @Output() public renderLinkEvent = new EventEmitter<{
    productId: string | undefined;
    categoryId: string | undefined;
  }>();

  public pieceSrc$!: Observable<string | undefined>;
  private readonly clearPieceSrcSubscription$ = new Subject<void>();

  constructor(
    private readonly assetsSharedService: AssetsSharedService,
    private readonly renderConfigurationService: RenderConfigurationService,
    private readonly linksService: LinksService
  ) {
    super();
  }

  public get newWindow(): boolean | undefined {
    return this.link.newWindow;
  }

  public get displayAnalyticsAction(): string | undefined {
    const analyticAction = this.link.analyticsAction;

    if (analyticAction && analyticAction.length > 0) {
      return this.link.customAnalyticsAction ?? analyticAction;
    }

    return undefined;
  }

  public ngOnInit(): void {
    this.initPieceSrc();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.link && changes.link.previousValue !== changes.link.currentValue) {
      this.initPieceSrc();
    }
  }

  public ngOnDestroy(): void {
    super.ngOnDestroy();
    this.clearPieceSrcSubscription$.next();
    this.clearPieceSrcSubscription$.complete();
  }

  public renderLinkClicked(e: Event): void {
    if (this.link.type === 'customEvent' && this.link.eventCode) {
      e.preventDefault();
      try {
        // Use of `new Function(...)` is required to support dynamic custom JS code in a controlled CMS context
        // eslint-disable-next-line @typescript-eslint/no-implied-eval, no-new-func
        new Function(this.link.eventCode)();
      } catch (error) {
        console.error('Failed to execute code event', error);
      }
    }

    this.renderLinkEvent.emit({
      productId: this.link.productId,
      categoryId: this.link.categoryId
    });
  }

  private initPieceSrc(): void {
    this.clearPieceSrcSubscription$.next();
    this.pieceSrc$ = this.getPieceSrc$();
    this.pieceSrc$.pipe(
      takeUntil(merge(this.clearSubscriptions$, this.clearPieceSrcSubscription$))
    ).subscribe((src) => {
      this.link = {
        ...this.link,
        src
      };
    });
  }

  private getPieceSrc$(): Observable<string | undefined> {
    const pieceName = this.piece ? this.piece.name : '';
    if (this.link.type === 'file') {
      return of(this.assetsSharedService.getAssetUrl(this.link.src, this.renderConfigurationService.config.assetsUrl));
    }
    const link = { pieceName, ...this.link };
    return this.linksService.getLinkUrl(link);
  }

}
